import * as React from "react";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { HelmetProvider } from "react-helmet-async";
import { ClerkProvider } from "@clerk/clerk-react";
import { frFR } from "@clerk/localizations";
import App from "./App";
import "./index.css";
import "./bright-theme.css";
import { I18nextProvider } from "react-i18next";
import i18n from "./lib/i18n";
import { ClerkAuthSync } from "./components/ClerkAuthSync";
import { initMixpanel } from "./lib/mixpanel";

// Clerk publishable key
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  console.error("Missing VITE_CLERK_PUBLISHABLE_KEY environment variable. Authentication will not work.");
  if (process.env.NODE_ENV === "development") {
    const errorDiv = document.createElement("div");
    errorDiv.style.cssText = "position:fixed;top:0;left:0;right:0;background:#dc2626;color:white;padding:12px;z-index:9999;text-align:center;font-family:sans-serif;";
    errorDiv.innerHTML = "⚠️ Missing VITE_CLERK_PUBLISHABLE_KEY. Add it to your .env file to enable authentication.";
    document.body.prepend(errorDiv);
  }
}

// Get browser language and check if it's supported
const browserLang = navigator.language.split("-")[0];
const supportedLangs = ["fr", "en"];
const defaultLang = supportedLangs.includes(browserLang) ? browserLang : "fr";

// Only set language if not already set
if (!localStorage.getItem("i18nextLng")) {
  localStorage.setItem("i18nextLng", defaultLang);
}

// Add error handling for i18n initialization
try {
  // Set the language in i18n without reinitializing
  i18n.changeLanguage(localStorage.getItem("i18nextLng") || defaultLang);
  console.log("i18n initialized successfully with language:", i18n.language);
} catch (error) {
  console.error("Error initializing i18n:", error);
}

// Apply theme from localStorage
const applyThemeFromStorage = () => {
  const savedTheme = localStorage.getItem("weklean-theme");
  const root = document.documentElement;

  // Remove all theme classes first
  root.classList.remove("bright-theme", "dark");

  // Apply the saved theme
  if (savedTheme === "bright") {
    root.classList.add("bright-theme");
  } else if (savedTheme === "dark") {
    root.classList.add("dark");
  }
};

// Apply theme immediately
applyThemeFromStorage();

// Initialize Mixpanel analytics
initMixpanel();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      localization={frFR}
      appearance={{
        elements: {
          formButtonPrimary: "bg-primary hover:bg-primary/90",
          card: "shadow-md",
          headerTitle: "text-foreground",
          headerSubtitle: "text-muted-foreground",
          socialButtonsBlockButton: "border border-input",
          formFieldLabel: "text-foreground",
          formFieldInput: "border-input",
          footerActionLink: "text-primary hover:text-primary/90",
        },
      }}
      signInUrl="/login"
      signUpUrl="/register"
      afterSignOutUrl="/"
    >
      <HelmetProvider>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <ClerkAuthSync>
              <App />
            </ClerkAuthSync>
            <Toaster />
          </QueryClientProvider>
        </I18nextProvider>
      </HelmetProvider>
    </ClerkProvider>
  </StrictMode>
);
